import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTrash,
  FaExclamationTriangle,
  FaSync,
} from "react-icons/fa";
import {
  getClasses,
  addClass,
  updateClass,
  deleteClass,
} from "../../services/classService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ClassLayer.css";

const ErrorToast = ({ message }) => {
  const displayMsg = typeof message === "string" 
    ? message 
    : (message && typeof message === "object" ? Object.values(message).flat().join(", ") : JSON.stringify(message) || "Something went wrong");
    
  return (
    <div className="d-flex align-items-start gap-2">
      <div className="text-danger mt-1 fs-5">
        <FaExclamationTriangle />
      </div>
      <div>
        <div className="fw-bold text-danger mb-1">Action Failed</div>
        <div style={{ fontSize: '13.5px', color: '#444' }}>{displayMsg}</div>
      </div>
    </div>
  );
};

const ClassLayer = () => {
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ class_name: "" });

  const fetchClasses = async () => {
    const res = await getClasses();
    if (res?.status) {
      setClasses(res.data || []);
    } else {
      toast.error("Fetch failed");
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.class_name) {
      toast.error("Enter class name");
      return;
    }

    const res = editId ? await updateClass(editId, form) : await addClass(form);

    if (res?.status) {
      toast.success(editId ? "Updated" : "Created");
      closeModal();
      fetchClasses();
    } else {
      const message = res?.message || "Something went wrong";
      toast.error(<ErrorToast message={message} />, { icon: false });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete class?")) return;

    const res = await deleteClass(id);

    if (res?.status) {
      toast.success("Deleted");
      fetchClasses();
    } else {
      toast.error("Delete failed");
    }
  };

  const handleEdit = (item) => {
    setForm({ class_name: item.class_name });
    setEditId(item.id);
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setEditId(null);
    setForm({ class_name: "" });
  };

  const filteredClasses = useMemo(() => {
    return classes.filter((item) => {
      const matchesSearch = item.class_name.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [classes, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredClasses.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedClasses = filteredClasses.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          <div className="admin-page-header list-header">
            <div>
              <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
                <div style={{ width: '6px', height: '18px', backgroundColor: '#4f46e5', borderRadius: '3px' }}></div>
                Class List
              </h6>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle" onClick={fetchClasses} style={{ width: '40px', height: '40px' }} title="Refresh">
                <FaSync className="text-secondary" />
              </button>
              <button className="btn-primary" onClick={() => setModal(true)}>
                <FaPlus /> Add Class
              </button>
            </div>
          </div>

          <div className="row g-3 align-items-end">
          <div className="col-12 col-md-4 col-lg-4">
            <label className="fw-semibold text-muted d-block mb-2">Search</label>
            <div className="admin-search search-box">
              <FaSearch />
              <input
                className="search-input"
                placeholder="Search by class name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="col-12 col-md-4 col-lg-3">
            <label className="fw-semibold text-muted d-block mb-2">Status</label>
            <select
              className="admin-filter w-100"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="col-12 col-md-4 col-lg-2">
            <label className="fw-semibold text-muted d-block mb-2">Per Page</label>
            <select
              className="admin-filter w-100"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

          <div className="table-card admin-table-card admin-table-scroll admin-responsive-table">
          <table className="user-table custom-table mt-3 text-center align-middle">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th className="text-center">Class</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedClasses.length > 0 ? (
                paginatedClasses.map((item, index) => (
                  <tr key={item.id} className="smooth-table-row">
                    <td data-label="No.">{startIndex + index + 1}</td>
                    <td data-label="Class">
                      <span className="badge-class">{item.class_name}</span>
                    </td>
                    <td data-label="Status">
                      <span
                        className={
                          item.status === "new" ? "badge-status-new" : "badge-status-assign"
                        }
                      >
                        {item.status}
                      </span>
                    </td>
                    <td data-label="Action">
                      <div className="table-actions actions d-flex justify-content-center">
                        <button
                          type="button"
                          className="table-icon-btn"
                          onClick={() => handleEdit(item)}
                          aria-label="Edit class"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          className="table-icon-btn danger"
                          onClick={() => handleDelete(item.id)}
                          aria-label="Delete class"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center p-4 empty-row">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

          <div className="admin-pagination">
          <div className="admin-pagination-info">
            Showing {filteredClasses.length ? startIndex + 1 : 0} to {Math.min(startIndex + pageSize, filteredClasses.length)} of {filteredClasses.length} classes
          </div>

          <div className="admin-pagination-controls">
            <button
              type="button"
              className="admin-page-btn"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <FaChevronLeft />
            </button>

            {Array.from({ length: totalPages }, (_, index) => index + 1)
              .slice(0, 5)
              .map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  className={`admin-page-btn ${currentPage === pageNumber ? "active" : ""}`}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}

            <button
              type="button"
              className="admin-page-btn"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay">
          <div className="glass-modal smooth-modal">
            <div className="modal-header">
              <h4>{editId ? "Update" : "Create"} Class</h4>
              <button type="button" className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <label className="input-label" htmlFor="class_name">
                Class Name
              </label>
              <input
                id="class_name"
                className="form-control-custom mt-3"
                placeholder="Class Name"
                value={form.class_name}
                onChange={(event) => setForm({ class_name: event.target.value })}
              />

              <div className="d-flex flex-column flex-sm-row gap-3 mt-4">
                <button type="button" className="btn-cancel flex-grow-1 m-0" onClick={closeModal}>
                  Cancel
                </button>
                <button className="btn-submit flex-grow-1 m-0">
                  {editId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={1000} />
    </div>
  );
};

export default ClassLayer;
